import { useState, useCallback, useMemo } from 'react';
import { useModal } from './useModal';
import { useHashModal } from './useHashModal';

/**
 * Generic hook for entity CRUD actions (Create, Read, Update, Delete)
 * 
 * Provides a reusable pattern for managing entity actions with modals, state management,
 * and action handlers. Supports customization through configuration and additional actions.
 * 
 * @example Basic usage:
 * ```typescript
 * const actions = useEntityActions<User>({
 *   entityName: 'User',
 *   displayProperty: 'name',
 *   deleteMutation: useDeleteUserMutation,
 *   getEntityId: (user) => user.id,
 * });
 * ```
 * 
 * @example With additional actions:
 * ```typescript
 * type UserAction = BaseEntityAction | 'activate' | 'deactivate';
 * 
 * const actions = useEntityActions<User, UserAction>({
 *   entityName: 'User',
 *   displayProperty: 'name',
 *   deleteMutation: useDeleteUserMutation,
 *   getEntityId: (user) => user.id,
 *   additionalActions: {
 *     activate: (user) => activateUser(user.id),
 *     deactivate: (user) => deactivateUser(user.id),
 *   },
 * });
 * ```
 */

// Base action type that can be extended
export type BaseEntityAction = 'view' | 'edit' | 'delete';

// Additional action can be either:
// - string: maps to navigateToModal(id, actionString)
// - function: custom logic with access to entity and navigateToModal
export type AdditionalAction<TEntity> = 
  | string 
  | ((entity: TEntity, navigateToModal?: (id: number, action: string) => void) => void);

// Configuration interface for the generic hook
export interface EntityActionsConfig<TEntity, TAction extends string = BaseEntityAction> {
  entityName: string;
  displayProperty: keyof TEntity;
  deleteMutation: any; // RTK Query mutation hook
  getEntityId: (entity: TEntity) => number;
  additionalActions?: Record<string, AdditionalAction<TEntity>>;
}

// Generic hook for entity CRUD actions
export const useEntityActions = <
  TEntity extends Record<string, any>,
  TAction extends string = BaseEntityAction
>(config: EntityActionsConfig<TEntity, TAction>) => {
  const {
    entityName,
    displayProperty,
    deleteMutation,
    getEntityId,
    additionalActions
  } = config;

  // We rely on consumers passing stable props (defined outside component)
  // The internal useMemo here was not actually fixing unstable inputs, just adding overhead.

  // Always use hash navigation
  const hashModal = useHashModal();

  // State management
  const [editingEntity, setEditingEntity] = useState<TEntity | null>(null);
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TEntity | null>(null);

  // Modal management
  const deleteConfirmModal = useModal();

  // RTK Query mutation
  const [deleteEntity, { isLoading: isDeleting }] = deleteMutation();

  // Base action handlers
  const handleCreateNew = useCallback(() => {
    hashModal.navigateToModal('new');
  }, [hashModal.navigateToModal]);

  const handleEdit = useCallback((entity: TEntity) => {
    setEditingEntity(entity);
    // Note: This is for backward compatibility, hash navigation handles the actual modal opening
  }, []);

  const handleView = useCallback((entity: TEntity) => {
    setSelectedEntityId(getEntityId(entity));
  }, [getEntityId]);

  const handleDeleteRequest = useCallback((entity: TEntity) => {
    setDeleteTarget(entity);
    deleteConfirmModal.open();
  }, [deleteConfirmModal]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return false;
    
    try {
      await deleteEntity(getEntityId(deleteTarget)).unwrap();
      deleteConfirmModal.close();
      setDeleteTarget(null);
      return true;
    } catch (error) {
      console.error('Delete error:', error);
      return false;
    }
  }, [deleteTarget, deleteEntity, deleteConfirmModal, getEntityId]);

  // Enhanced modal close handlers (for hash modals, this is handled by hash navigation)

  const handleCloseDeleteModal = useCallback(() => {
    deleteConfirmModal.close();
    setDeleteTarget(null);
  }, [deleteConfirmModal]);

  // Generic action handler with hash navigation
  const handleAction = useCallback((action: TAction, entity: TEntity) => {
    switch (action as BaseEntityAction) {
      case 'view':
        hashModal.navigateToModal(getEntityId(entity), 'view');
        break;
      case 'edit':
        hashModal.navigateToModal(getEntityId(entity), 'edit');
        break;
      case 'delete':
        // Delete still uses traditional modal for confirmation
        handleDeleteRequest(entity);
        break;
      default:
        // Handle additional actions with hash navigation
        if (additionalActions && action in additionalActions) {
          const actionValue = additionalActions[action];
          if (typeof actionValue === 'string') {
            hashModal.navigateToModal(getEntityId(entity), actionValue);
          } else {
            actionValue(entity, hashModal.navigateToModal);
          }
        } else {
          console.warn(`Unknown action: ${action}`);
        }
    }
  }, [hashModal, getEntityId, handleDeleteRequest, additionalActions]);

  // Convenient handler for row clicks that opens the view modal
  const handleRowClick = useCallback((entity: TEntity) => {
    handleAction('view' as TAction, entity);
  }, [handleAction]);

  // Memoized return object
  return useMemo(() => ({
    // State
    selectedEntityId,
    editingEntity,
    deleteTarget,
    isDeleting,
    
    // Modal props objects (create/edit now handled by hash navigation)
    
    detailsModalProps: {
      isOpen: !!selectedEntityId,
      onClose: () => setSelectedEntityId(null),
      selectedEntity: null, // Will be set in component
    },
    
    deleteConfirmModalProps: {
      isOpen: deleteConfirmModal.isOpen,
      onClose: handleCloseDeleteModal,
      title: `Delete ${entityName}`,
      message: `Are you sure you want to delete "${deleteTarget?.[displayProperty]}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      isLoading: isDeleting,
      type: "danger" as const,
    },
    
    // Actions
    handleCreateNew,
    handleView,
    handleEdit,
    handleDeleteRequest,
    handleDelete,
    handleAction,
    handleRowClick,
    
    // Hash navigation
    navigateToModal: hashModal.navigateToModal,
    
    // Setters for advanced usage
    setSelectedEntityId,
    setEditingEntity,
    setDeleteTarget,
  }), [
    selectedEntityId,
    editingEntity,
    deleteTarget,
    isDeleting,
    deleteConfirmModal.isOpen,
    handleCloseDeleteModal,
    entityName,
    deleteTarget?.[displayProperty],
    handleCreateNew,
    handleView,
    handleEdit,
    handleDeleteRequest,
    handleDelete,
    handleAction,
    handleRowClick,
    hashModal.navigateToModal,
  ]);
}; 